// Prints a themed "Mission Accomplished" star-registry certificate.
// `rocketSvg` is optional SVG markup of the pupil's own ship (falls back to 🚀).
export function printCertificate({ pupilName, projectName, className, color = "#3B82F6", percent, rocketSvg }) {
  const esc = (s) => String(s == null ? "" : s).replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
  color = /^#[0-9A-Fa-f]{3,8}$/.test(String(color)) ? color : "#3B82F6";
  const date = new Date().toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" });
  const html = `<!doctype html><html><head><meta charset="utf-8"><title>${esc(pupilName)} — Certificate</title>
  <style>
    @page { size: A4 landscape; margin: 0; }
    * { box-sizing: border-box; }
    html, body { margin: 0; height: 100%; }
    body { font-family: Georgia, 'Times New Roman', serif; background: #0B0D1B;
           display: flex; align-items: center; justify-content: center; min-height: 100vh; padding: 24px; }
    .frame { position: relative; width: 100%; max-width: 1000px; aspect-ratio: 1.414/1;
             background: radial-gradient(ellipse at top, ${color}22, #0B0D1B 70%), #0B0D1B;
             border-radius: 22px; color: #fff; overflow: hidden;
             box-shadow: 0 0 60px ${color}88; }
    /* Gold-foil gradient border ring */
    .foil { position: absolute; inset: 0; border-radius: 22px; border: 12px solid transparent;
            background: linear-gradient(rgba(11,13,27,0), rgba(11,13,27,0)) padding-box,
                        linear-gradient(135deg, #7a5c12, #ffe680 25%, #fff7cf 50%, #ffd23f 75%, #7a5c12) border-box;
            box-shadow: inset 0 0 16px #ffd23f44, 0 0 0 2px ${color}55; }
    .inner { position: absolute; inset: 26px; border: 2px solid #ffd23f66; border-radius: 14px;
             display: flex; flex-direction: column; align-items: center; justify-content: center; text-align: center; padding: 40px; }
    .stars { position: absolute; inset: 0; background-image:
             radial-gradient(1.5px 1.5px at 20% 30%, #fff, transparent),
             radial-gradient(1.5px 1.5px at 70% 20%, #fff, transparent),
             radial-gradient(2px 2px at 40% 70%, #fff, transparent),
             radial-gradient(1.5px 1.5px at 85% 65%, #fff, transparent),
             radial-gradient(1.5px 1.5px at 55% 45%, #fff, transparent),
             radial-gradient(2px 2px at 15% 80%, #fff, transparent);
             opacity: .5; }
    .corner { position: absolute; font-size: 26px; color: #ffd23f; text-shadow: 0 0 10px #ffd23f; z-index: 3; }
    .tl { top: 30px; left: 34px; } .tr { top: 30px; right: 34px; }
    .bl { bottom: 30px; left: 34px; } .br { bottom: 30px; right: 34px; }
    .rocket { font-size: 60px; margin-bottom: 4px; line-height: 1; }
    .rocket svg { width: 116px; height: 116px; filter: drop-shadow(0 0 16px ${color}); }
    .eyebrow { letter-spacing: 8px; text-transform: uppercase; font-size: 15px; color: ${color}; margin-bottom: 4px; }
    h1 { font-family: 'Trebuchet MS', sans-serif; font-size: 44px; margin: 0 0 4px; color: ${color}; text-shadow: 0 0 24px ${color}; }
    .presented { font-size: 15px; color: #cbd5e1; margin: 14px 0 6px; }
    .name { font-family: 'Trebuchet MS', sans-serif; font-size: 50px; font-weight: bold; margin: 0;
            border-bottom: 3px solid #ffd23f; padding: 0 24px 8px; display: inline-block; }
    .body { font-size: 17px; color: #e2e8f0; margin: 20px auto 0; max-width: 72%; line-height: 1.5; }
    .project { color: ${color}; font-weight: bold; }
    .foot { position: absolute; bottom: 40px; left: 0; right: 0; display: flex; justify-content: space-between; padding: 0 72px; font-size: 13px; color: #94a3b8; z-index: 2; }
    .foot b { color: #fff; }
    .seal { position: absolute; bottom: 58px; left: 50%; transform: translateX(-50%);
            width: 90px; height: 90px; border-radius: 50%; border: 3px solid #ffd23f;
            display: flex; align-items: center; justify-content: center; font-size: 38px;
            background: #ffd23f22; box-shadow: 0 0 24px #ffd23f88; z-index: 2; }
    @media print { body { background: #0B0D1B; -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
  </style></head><body>
    <div class="frame">
      <div class="stars"></div>
      <div class="foil"></div>
      <span class="corner tl">⭐</span><span class="corner tr">⭐</span>
      <span class="corner bl">⭐</span><span class="corner br">⭐</span>
      <div class="inner">
        <div class="rocket">${rocketSvg || "🚀"}</div>
        <div class="eyebrow">Stellar Steps</div>
        <h1>Mission Accomplished</h1>
        <div class="presented">This certifies that</div>
        <p class="name">${esc(pupilName)}</p>
        <p class="body">has successfully completed the <span class="project">${esc(projectName)}</span> mission${percent != null ? ` with a stellar Exploration Test score of <b>${esc(percent)}%</b>` : ""} and had their work marked to the highest standard. You own a star! ⭐</p>
        <div class="seal">⭐</div>
        <div class="foot"><span>Class: <b>${esc(className)}</b></span><span>Awarded: <b>${esc(date)}</b></span></div>
      </div>
    </div>
    <script>window.onload = () => setTimeout(() => window.print(), 350);</script>
  </body></html>`;
  const blob = new Blob([html], { type: "text/html" });
  const url = URL.createObjectURL(blob);
  const w = window.open(url, "_blank");
  if (!w) { URL.revokeObjectURL(url); return; }
  w.focus();
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}
